import levenshtein from "js-levenshtein";

import { GbpCategory } from "../../../../../../models/GbpCategory";
import DataSvc from "../../../../../../services/dataSvc";
import { Schema } from "../../schema";

export class CategoryAutocompleter {
  static current: CategoryAutocompleter | undefined;

  // Key = human readable name
  // In cases where there is a duplicate the key will be the `${Japanese name} (${decomposed english name})`
  // Value = gbp_category_id
  private readonly optionDict: Record<string, string> = {};
  private readonly selectableOptions: string[] = [];

  constructor(gbpCategories: GbpCategory[]) {
    gbpCategories.forEach((category) => {
      const { name, gbp_category_id } = category;

      this.optionDict[name] = gbp_category_id;
      this.selectableOptions.push(name);
    });
  }

  static async initialize() {
    if (CategoryAutocompleter.current) {
      return;
    }
    const gbpCategories = await DataSvc.getGbpCategories();

    CategoryAutocompleter.current = new CategoryAutocompleter(gbpCategories);
  }

  static async performAutocomplete(rawInput: string) {
    if (!CategoryAutocompleter.current) {
      await CategoryAutocompleter.initialize();
    }

    if (!rawInput || !CategoryAutocompleter.current) {
      return [];
    }

    const input = rawInput.toLowerCase();

    // This algorithm's goal is to find the most similar options to auto-suggest
    // It can be improved if necessary
    const relevantOptions = CategoryAutocompleter.current.selectableOptions
      .filter((category) => category.toLowerCase().includes(input)) // Filter out options that don't contain the full input (not ideal but speeds up the algorithm)
      .sort((a, b) => {
        const length = Math.min(a.length, b.length, input.length);

        const _a = a.slice(0, length);
        const _b = b.slice(0, length);
        const _input = input.slice(0, length);

        const aDistance = levenshtein(_a, _input);
        const bDistance = levenshtein(_b, _input);

        const distance = aDistance - bDistance;

        // If the distance is 0, that means that both strings are equally close to the input,
        // so return the shorter string
        if (distance === 0) {
          return a.length - b.length;
        }

        // Otherwise, return the more similar string
        return distance;
      })
      .slice(0, 5); // Only include the closest 5 options

    return relevantOptions;
  }

  static async createUpdatePayload(data: Schema) {
    if (!CategoryAutocompleter.current) {
      await CategoryAutocompleter.initialize();
    }

    const autocompleter = CategoryAutocompleter.current;

    const { primaryCategory, additionalCategories } = data;

    const payload = {
      location: {
        categories: {
          primaryCategory: {
            name: autocompleter?.optionDict[primaryCategory.name]
          },
          additionalCategories: additionalCategories.map((category) => ({
            name: autocompleter?.optionDict[category.name]
          }))
        }
      }
    };

    return payload;
  }

  checkIsValidCategory(category: string) {
    return this.selectableOptions.includes(category);
  }
}
